import {
    StatsigProvider,
    StatsigSynchronousProvider,
} from '@eventbrite/statsig';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React, { useMemo } from 'react';
import {
    EventBasicInformationProvider,
    getTrackingContextFrom,
    ScrollContextProvider,
    TicketContextProvider,
    TrackingContext,
    type EventBasicInformation,
} from './contexts';
import { CommunicationProvider } from './contexts/CommunicationContext';

import { SSRExperimentationProvider } from './contexts/ExperimentationContext/context';
import { FeatureFlagsProvider } from './contexts/FeatureFlags';
import { ListingTimedEntryExperimentContextProvider } from './contexts/ListingTimedEntryExperimentContext';
import {
    OrganizerContextState,
    OrganizerProvider,
} from './contexts/OrganizerContext';
import type { TicketDataType } from './contexts/TicketContext/types';
import { ServerAppProps } from './server';
import type { ChildEventProps, EventListingsResponse } from './types';
import {
    getLocationPlaceId,
    isEventSalesEnded,
    isSoldOutEvent,
    mapTimedEntryInitialEvents,
} from './utils';

export type ListingsProvidersProps = React.PropsWithChildren<
    Pick<
        ServerAppProps,
        | 'event'
        | 'eventDescription'
        | 'eventMapLayout'
        | 'eventDetails'
        | 'user'
        | 'settings'
        | 'env'
        | 'request'
        | 'statsig'
        | 'conversionBar'
        | 'tickets'
        | 'urgencySignals'
        | 'eventSessionResponse'
        | 'parentEventId'
        | 'featureFlags'
        | 'discount'
        | 'experimentation'
        | 'organizer'
    >
>;

function chooseChildEventsSource(
    childEvents: Array<ChildEventProps> | null,
    transformedEventSessions: Array<ChildEventProps> | undefined,
    featureFlag: EventListingsResponse['featureFlags'],
) {
    if (featureFlag?.enableTimedEntry) {
        return transformedEventSessions || [];
    }

    return childEvents || [];
}

export function ListingsProviders({
    children,
    event,
    eventDescription,
    eventMapLayout,
    eventDetails,
    user,
    settings,
    env,
    request,
    statsig,
    conversionBar,
    tickets,
    urgencySignals,
    eventSessionResponse,
    parentEventId,
    featureFlags,
    discount,
    experimentation,
    organizer,
}: ListingsProvidersProps) {
    const ticketServerData: TicketDataType = {
        compactCheckoutDisqualifications:
            event.compactCheckoutDisqualifications,
        panelDisplayPrice: conversionBar.panelDisplayPrice,
        ...tickets,
    };
    const isSoldOut = isSoldOutEvent({
        event,
        eventListingResponse: {
            eventSessions: eventSessionResponse,
            parentEventId: parentEventId,
        },
    });

    const isSalesEndedEvent = isEventSalesEnded({
        event,
        eventListingResponse: {
            eventSessions: eventSessionResponse,
            parentEventId: parentEventId,
        },
    });

    const { transformedEventSessions } = mapTimedEntryInitialEvents(
        eventSessionResponse,
        event.id,
        env.localeInfo.locale,
        event.start.timezone,
        event.isOnlineEvent,
    );
    const eventBasicInfo: EventBasicInformation = {
        childEvents: chooseChildEventsSource(
            event.childEvents,
            transformedEventSessions,
            featureFlags,
        ),
        id: event.id,
        isSeriesParent: event.isSeriesParent,
        name: event.name,
        summary: eventDescription.summary,
        url: event.url,
        start: event.start,
        end: event.end,
        place: {
            isOnline: event.isOnlineEvent,
            latitude: eventMapLayout.location.latitude,
            longitude: eventMapLayout.location.longitude,
            placeId: getLocationPlaceId(eventDetails?.location),
        },
        salesStatus: event.salesStatus['sales_status'],
        shouldDisplayCrawlableSeriesChildrenLinks:
            eventDetails.dates.shouldDisplayCrawlableSeriesChildrenLinks,
        ticketsInfo: event.ticketsInfo,
        discount: discount,
        locale: env.localeInfo.locale,
        tld: env.localeInfo.tld,
        parentEventId: parentEventId,
        startDateTime: event.startDateTime,
        isSoldOut: isSoldOut,
        isRepeating: event.isRepeating,
        currentEventSessionsTimedEntry: transformedEventSessions,
        hideStartDate: event.hideStartDate,
        hideEndDate: event.hideEndDate,
        isSalesEndedEvent: isSalesEndedEvent,
        isEnded: event.isEnded,
        serverUrl: env.serverUrl,
    };

    const cacheClient = useMemo(() => new QueryClient(), []);

    const shouldUseSyncProvider =
        settings.featureFlags?.enableSSRExperimentationOnListings &&
        statsig.customParams?.SSRBatch === 'True';

    const StatigProvider = shouldUseSyncProvider
        ? StatsigSynchronousProvider
        : StatsigProvider;

    const statsigProps = shouldUseSyncProvider
        ? {
              initializeValues: statsig.initializeValues,
              user: {
                  custom: statsig.customParams,
              },
              options: {
                  overrideStableID: statsig.stableId || undefined,
              },
              userFields: new Set([
                  'custom',
                  'appVersion',
                  'deviceType',
                  'locale',
                  'userAgent',
              ] as any[]),
          }
        : { deactivateSdk: true };

    const organizerContextState: OrganizerContextState = {
        basicInfo: {
            id: organizer.id,
            name: organizer.name,
            avatar: organizer.profilePicture,
            url: organizer.url,
            description: organizer.description,
        },
        socialInfo: {
            website: organizer.orgWebsite,
            facebook: organizer.orgFacebook,
            twitter: organizer.orgTwitter,
        },
    };

    return (
        <StatigProvider {...statsigProps}>
            <QueryClientProvider client={cacheClient}>
                <FeatureFlagsProvider
                    featureFlags={{ ...settings.featureFlags, ...featureFlags }}
                >
                    <TicketContextProvider
                        ticketServerData={ticketServerData}
                        externalTicketsAreAvailable={
                            conversionBar.externalTicketsAreAvailable
                        }
                    >
                        <EventBasicInformationProvider value={eventBasicInfo}>
                            <TrackingContext.Provider
                                value={getTrackingContextFrom({
                                    user,
                                    event,
                                    settings,
                                    env,
                                    request,
                                    components: { eventDetails },
                                    urgencySignals,
                                })}
                            >
                                <ScrollContextProvider>
                                    <CommunicationProvider>
                                        <ListingTimedEntryExperimentContextProvider
                                            sessionsLength={
                                                eventSessionResponse?.length ||
                                                0
                                            }
                                            seriesInfiniteCalendarSplitFF={
                                                settings.featureFlags
                                                    ?.seriesInfiniteCalendar ||
                                                false
                                            }
                                            parentEventId={
                                                eventBasicInfo.parentEventId
                                            }
                                            childEventLength={
                                                eventBasicInfo.childEvents
                                                    ?.length || 0
                                            }
                                        >
                                            <SSRExperimentationProvider
                                                value={experimentation}
                                            >
                                                <OrganizerProvider
                                                    initialState={
                                                        organizerContextState
                                                    }
                                                >
                                                    {children}
                                                </OrganizerProvider>
                                            </SSRExperimentationProvider>
                                        </ListingTimedEntryExperimentContextProvider>
                                    </CommunicationProvider>
                                </ScrollContextProvider>
                            </TrackingContext.Provider>
                        </EventBasicInformationProvider>
                    </TicketContextProvider>
                </FeatureFlagsProvider>
            </QueryClientProvider>
        </StatigProvider>
    );
}
